import { useState } from "react";
import type { Programa } from "@/interfaces/programa.interface";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Trash2,
  Pencil,
  Users2,
  Loader2,
  Search,
  AlertCircle,
  UserPlus,
  Upload,
  Download,
} from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { useSendActivationEmails } from "@/hooks/aprendices/useSendActivationEmails";
import { toast } from "sonner";
import { axiosInstance } from "@retro-ia/shared/axios";
import { AsignarActividadesModal } from "./AsignarActividadesModal";
import { ProgramaAprendizEditDialog } from "./ProgramaAprendizEditDialog";
import AprendicesBulkActionsToolbar from "./AprendicesBulkActionsToolbar";
import { RegisterAprendizWithUsuarioForm } from "@/components/forms/aprendices/RegisterAprendizWithUsuarioForm";
import { UploadExcelAprendices } from "@/components/forms/aprendices/UploadExcelAprendices";
import {
  useAprendicesConActividades,
  useAsignacionMasiva,
  useRemoveAprendicesFromPrograma,
} from "@/hooks/programas/useGestionAprendices";
import { SectionHeader } from "@/components/layout/SectionHeader";

interface ProgramaAprendicesProps {
  idPrograma: number;
  programa: Programa;
  isSuperAdmin: boolean;
}

export function ProgramaAprendices({
  idPrograma,
  programa,
  isSuperAdmin,
}: ProgramaAprendicesProps) {
  const [filtro, setFiltro] = useState<"todos" | "sin-asignar" | number>(
    "todos",
  );
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [asignarModalOpen, setAsignarModalOpen] = useState(false);
  const [modalRegisterOpen, setModalRegisterOpen] = useState(false);
  const [modalUploadOpen, setModalUploadOpen] = useState(false);
  const [editingAprendizId, setEditingAprendizId] = useState<number | null>(
    null,
  );

  const {
    data: datosAprendices,
    isLoading: isLoadingAprendices,
    refetch: refetchAprendices,
  } = useAprendicesConActividades(idPrograma, true);

  const asignacionMasiva = useAsignacionMasiva();

  const { mutate: removerAprendices, isPending: isRemoving } =
    useRemoveAprendicesFromPrograma(idPrograma, {
      onSuccess: () => setSelectedIds(new Set()),
    });

  const { mutate: enviarCorreosActivacion, isPending: isSendingEmails } =
    useSendActivationEmails({
      queryKey: ["aprendices-con-actividades", idPrograma],
      onSuccess: () => setSelectedIds(new Set()),
    });

  const { mutateAsync: exportUsers } = useMutation<Blob, Error, number>({
    mutationFn: async (idPrograma: number) => {
      const res = await axiosInstance.get(
        `/aprendices-programas/programa/${idPrograma}/exportar-usuarios`,
        { responseType: "blob" },
      );
      return res.data;
    },
  });

  const actividades = datosAprendices?.actividades || [];
  const aprendices = datosAprendices?.aprendices || [];
  const resumen = datosAprendices?.resumen || {
    total: 0,
    sinAsignar: 0,
    porActividad: {},
  };

  const aprendicesFiltrados = aprendices.filter((aprendiz) => {
    if (search) {
      const searchLower = search.toLowerCase();
      const matchNombre = aprendiz.nombre.toLowerCase().includes(searchLower);
      const matchCorreo = aprendiz.correo.toLowerCase().includes(searchLower);
      if (!matchNombre && !matchCorreo) return false;
    }

    if (filtro === "todos") return true;
    if (filtro === "sin-asignar") return aprendiz.actividades.length === 0;

    return aprendiz.actividades.some((act) => act.idActividad === filtro);
  });

  const toggleSelect = (id: number) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const toggleSelectAll = () => {
    const visibleIds = new Set(aprendicesFiltrados.map((a) => a.id));
    if (selectedIds.size === visibleIds.size && visibleIds.size > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(visibleIds);
    }
  };

  const allSelected =
    aprendicesFiltrados.length > 0 &&
    aprendicesFiltrados.every((a) => selectedIds.has(a.id));
  const someSelected = selectedIds.size > 0;

  const handleRemoverDelPrograma = () => {
    if (selectedIds.size === 0) return;

    const seleccionados = aprendices.filter((a) => selectedIds.has(a.id));

    if (!isSuperAdmin) {
      const conIntentos = seleccionados.filter((a) =>
        a.actividades.some((act) => act.intentos > 0),
      );
      if (conIntentos.length > 0) {
        const nombres = conIntentos.map((a) => a.nombre).join(", ");
        toast.error(
          `No puedes desasignar a los siguientes aprendices porque ya tienen intentos registrados: ${nombres}`,
          { duration: 6000 },
        );
        return;
      }
    }

    const names = seleccionados.map((a) => a.nombre).join(", ");
    if (
      !window.confirm(
        `¿Estás seguro de que deseas remover a ${names} del programa? Esta acción no se puede deshacer fácilmente.`,
      )
    )
      return;
    removerAprendices(Array.from(selectedIds));
  };

  const handleEnviarCorreos = () => {
    if (selectedIds.size === 0) return;
    enviarCorreosActivacion(Array.from(selectedIds));
  };

  const handleExportUsers = async () => {
    if (!programa?.id) return;
    try {
      const csvBlob = await exportUsers(programa.id);
      const url = window.URL.createObjectURL(csvBlob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `usuarios_programa_${programa.id}.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Usuarios exportados exitosamente.");
    } catch (error) {
      toast.error("Error al exportar usuarios.");
      console.error("Error exporting users:", error);
    }
  };

  const handleAsignar = (actividadesIds: number[], modo: "replace" | "add") => {
    if (selectedIds.size === 0 || !programa) return;

    let actividadesFinales = actividadesIds;

    if (!isSuperAdmin && modo === "replace") {
      const seleccionados = aprendices.filter((a) => selectedIds.has(a.id));
      const actividadesConIntentosIds = new Set<number>();
      const actividadesConIntentosNombres = new Set<string>();
      seleccionados.forEach((a) => {
        a.actividades.forEach((act) => {
          if (act.intentos > 0) {
            actividadesConIntentosIds.add(act.idActividad);
            actividadesConIntentosNombres.add(act.nombre);
          }
        });
      });

      if (actividadesConIntentosIds.size > 0) {
        const merged = new Set([
          ...actividadesIds,
          ...actividadesConIntentosIds,
        ]);
        actividadesFinales = Array.from(merged);
        const nombresProtegidos = Array.from(
          actividadesConIntentosNombres,
        ).join(", ");
        toast.warning(
          `Las siguientes actividades no pueden ser desasignadas porque ya tienen intentos registrados y se mantuvieron: ${nombresProtegidos}`,
          { duration: 8000 },
        );
      }
    }

    asignacionMasiva.mutate(
      {
        idPrograma: programa.id,
        aprendicesIds: Array.from(selectedIds),
        actividadesIds: actividadesFinales,
        modo,
      },
      {
        onSuccess: (data) => {
          const mensajes = [];
          if (data.asignados > 0) {
            mensajes.push(`${data.asignados} asignados`);
          }
          if (data.modificados > 0) {
            mensajes.push(`${data.modificados} modificados`);
          }
          if (data.desasignados > 0) {
            mensajes.push(`${data.desasignados} desasignados`);
          }
          if (data.errores.length > 0) {
            mensajes.push(`${data.errores.length} errores`);
          }

          toast.success(`Asignación completada: ${mensajes.join(", ")}`);
          setSelectedIds(new Set());
          refetchAprendices();
        },
        onError: (error: any) => {
          toast.error(
            error.response?.data?.message || "Error al realizar asignación",
          );
        },
      },
    );
  };

  const handleDesasignar = () => {
    if (!isSuperAdmin) {
      const seleccionados = aprendices.filter((a) => selectedIds.has(a.id));
      const conIntentos = seleccionados.filter((a) =>
        a.actividades.some((act) => act.intentos > 0),
      );
      if (conIntentos.length > 0) {
        const detalle = conIntentos
          .map((a) => {
            const actsConIntentos = a.actividades
              .filter((act) => act.intentos > 0)
              .map((act) => act.nombre)
              .join(", ");
            return `${a.nombre} (${actsConIntentos})`;
          })
          .join("; ");
        toast.error(
          `No puedes desasignar actividades de los siguientes aprendices porque ya tienen intentos registrados: ${detalle}`,
          { duration: 8000 },
        );
        return;
      }
    }
    handleAsignar([], "replace");
  };

  const getFiltroNombre = (): string => {
    if (filtro === "todos") return "Todos";
    if (filtro === "sin-asignar") return "Sin asignar";
    const actividad = actividades.find((a) => a.id === filtro);
    return actividad?.nombre || `Actividad ${filtro}`;
  };

  return (
    <section className="space-y-6">
      <SectionHeader
        icon={Users2}
        title="Gestión de Aprendices"
        description="Administra los participantes del programa. Registra nuevos aprendices, asigna actividades individualmente o en masa, y monitorea el progreso de cada estudiante."
      />

      <div className="rounded-2xl border border-black/5 bg-[var(--card-background)] p-6 shadow-lg dark:border-white/10">
        {/* Filtros y botones de acción */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <Select
            value={String(filtro)}
            onValueChange={(v) =>
              setFiltro(v === "todos" || v === "sin-asignar" ? v : Number(v))
            }
          >
            <SelectTrigger className="w-[280px]">
              <SelectValue placeholder="Seleccionar filtro" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos: {resumen.total}</SelectItem>
              <SelectItem value="sin-asignar">
                Sin asignar: {resumen.sinAsignar}
              </SelectItem>
              <SelectSeparator />
              {actividades.map((actividad) => (
                <SelectItem key={actividad.id} value={String(actividad.id)}>
                  {actividad.nombre}:{" "}
                  {resumen.porActividad[actividad.id] || 0}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Botones para agregar aprendices (solo cuando no hay selección) */}
          {selectedIds.size === 0 && (
            <>
              <Dialog
                open={modalRegisterOpen}
                onOpenChange={setModalRegisterOpen}
              >
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm">
                    <UserPlus className="h-4 w-4 mr-2" />
                    Registrar aprendiz
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>
                      Registrar aprendiz en el programa
                    </DialogTitle>
                  </DialogHeader>
                  <RegisterAprendizWithUsuarioForm
                    idPrograma={programa.id}
                    idClienteFinal={programa.idClienteFinal}
                    setOpen={setModalRegisterOpen}
                    queryKey={["aprendices-con-actividades", programa.id]}
                  />
                </DialogContent>
              </Dialog>

              <Button size="sm" onClick={handleExportUsers} variant="outline">
                <Download className="h-4 w-4 mr-2" />
                Exportar usuarios
              </Button>

              <Dialog
                open={modalUploadOpen}
                onOpenChange={setModalUploadOpen}
              >
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Upload className="h-4 w-4 mr-2" />
                    Cargar archivo
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Cargar aprendices masivamente</DialogTitle>
                  </DialogHeader>
                  <UploadExcelAprendices
                    idPrograma={programa.id}
                    idClienteFinal={programa.idClienteFinal}
                    setOpen={setModalUploadOpen}
                    queryKey={["aprendices-con-actividades", programa.id]}
                    actividades={programa.actividadPrograma?.map((act) => ({
                      id: act.id,
                      nombre: act.nombre,
                    }))}
                  />
                </DialogContent>
              </Dialog>
            </>
          )}
        </div>

        {/* Buscador y toolbar */}
        <div className="space-y-3 mb-4">
          <div className="flex items-center gap-3">
            <div className="relative flex-1 min-w-[280px]">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Buscar por nombre o correo..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 w-full"
              />
            </div>
          </div>

          {someSelected && (
            <AprendicesBulkActionsToolbar
              selectedCount={selectedIds.size}
              allSelected={allSelected}
              onToggleSelectAll={toggleSelectAll}
              onOpenAsignarModal={() => setAsignarModalOpen(true)}
              onDesasignar={handleDesasignar}
              onEnviarCorreos={handleEnviarCorreos}
              onRemoverDelPrograma={handleRemoverDelPrograma}
              isSendingEmails={isSendingEmails}
              isRemoving={isRemoving}
            />
          )}
        </div>

        {/* Modal de asignación de actividades */}
        <AsignarActividadesModal
          open={asignarModalOpen}
          onOpenChange={setAsignarModalOpen}
          actividades={actividades}
          actividadesActualesIds={
            selectedIds.size === 1
              ? aprendices
                  .find((a) => a.id === Array.from(selectedIds)[0])
                  ?.actividades.map((act) => act.idActividad) || []
              : []
          }
          onConfirm={handleAsignar}
        />

        <ProgramaAprendizEditDialog
          open={editingAprendizId !== null}
          onOpenChange={(open) => {
            if (!open) setEditingAprendizId(null);
          }}
          idAprendiz={editingAprendizId}
          queryKey={["aprendices-con-actividades", idPrograma]}
        />

        {/* Estado de carga */}
        {isLoadingAprendices ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <span className="ml-2 text-sm text-muted-foreground">
              Cargando aprendices...
            </span>
          </div>
        ) : (
          <>
            {/* Tabla de aprendices */}
            {aprendicesFiltrados.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <AlertCircle className="h-12 w-12 text-muted-foreground mb-3" />
                <p className="text-sm text-muted-foreground">
                  {search || filtro !== "todos"
                    ? "No se encontraron aprendices con este criterio"
                    : "No hay aprendices inscritos en este programa"}
                </p>
              </div>
            ) : (
              <div className="border rounded-lg overflow-auto">
                <Table>
                  <TableHeader className="sticky top-0 bg-background z-10">
                    <TableRow>
                      <TableHead className="w-[50px]">
                        <Checkbox
                          checked={allSelected}
                          onCheckedChange={toggleSelectAll}
                          aria-label="Seleccionar todos"
                        />
                      </TableHead>
                      <TableHead className="min-w-[150px]">
                        Usuario
                      </TableHead>
                      <TableHead className="min-w-[200px]">
                        Aprendiz
                      </TableHead>
                      <TableHead className="min-w-[250px]">Correo</TableHead>
                      <TableHead className="min-w-[300px]">
                        Actividades
                      </TableHead>
                      <TableHead className="min-w-[120px]">
                        Asignado el
                      </TableHead>
                      <TableHead className="min-w-[88px] w-[88px]">
                        Acciones
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {aprendicesFiltrados.map((aprendiz) => (
                      <TableRow
                        key={aprendiz.id}
                        className={
                          selectedIds.has(aprendiz.id)
                            ? "bg-muted/50"
                            : undefined
                        }
                      >
                        <TableCell className="p-3">
                          <Checkbox
                            checked={selectedIds.has(aprendiz.id)}
                            onCheckedChange={() => toggleSelect(aprendiz.id)}
                            aria-label={`Seleccionar ${aprendiz.nombre}`}
                          />
                        </TableCell>
                        <TableCell className="p-3 text-sm">
                          {aprendiz.username || "-"}
                        </TableCell>
                        <TableCell className="p-3 font-medium">
                          {aprendiz.nombre}
                        </TableCell>
                        <TableCell className="p-3 text-muted-foreground text-sm">
                          {aprendiz.correo}
                        </TableCell>
                        <TableCell className="p-3">
                          {aprendiz.actividades.length > 0 ? (
                            <div className="flex flex-wrap gap-1.5">
                              {aprendiz.actividades.map((act) => (
                                <Badge
                                  key={act.id}
                                  variant="secondary"
                                  className="text-xs shrink-0"
                                >
                                  {act.nombre}
                                </Badge>
                              ))}
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-xs italic">
                              Sin asignar
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="p-3 text-muted-foreground text-sm whitespace-nowrap">
                          {aprendiz.actividades.length > 0
                            ? new Date(
                                Math.max(
                                  ...aprendiz.actividades.map((a) =>
                                    new Date(a.asignadoEl).getTime(),
                                  ),
                                ),
                              ).toLocaleDateString()
                            : "-"}
                        </TableCell>
                        <TableCell className="p-3">
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="icon"
                              className="h-8 w-8 p-0"
                              type="button"
                              onClick={() =>
                                setEditingAprendizId(aprendiz.id)
                              }
                              aria-label={`Editar ${aprendiz.nombre}`}
                              title="Editar aprendiz"
                            >
                              <Pencil className="h-4 w-4 text-amber-500" />
                            </Button>
                            <Button
                              size="sm"
                              variant="icon"
                              className="h-8 w-8 p-0 text-white hover:bg-destructive/80"
                              disabled={isRemoving}
                              onClick={() => {
                                if (
                                  !isSuperAdmin &&
                                  aprendiz.actividades.some(
                                    (act) => act.intentos > 0,
                                  )
                                ) {
                                  toast.error(
                                    `No puedes desasignar a ${aprendiz.nombre} porque ya tiene intentos registrados.`,
                                    { duration: 5000 },
                                  );
                                  return;
                                }
                                if (
                                  window.confirm(
                                    `¿Estás seguro de que deseas remover a ${aprendiz.nombre} del programa?`,
                                  )
                                ) {
                                  removerAprendices([aprendiz.id]);
                                }
                              }}
                              aria-label={`Remover a ${aprendiz.nombre} del programa`}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Información de filtro activo */}
            {filtro !== "todos" && (
              <div className="text-sm text-muted-foreground mt-2">
                Mostrando {aprendicesFiltrados.length} de {resumen.total}{" "}
                aprendices{" "}
                {filtro === "sin-asignar"
                  ? "sin asignar"
                  : `en ${getFiltroNombre()}`}
                <button
                  onClick={() => setFiltro("todos")}
                  className="ml-2 text-primary hover:underline"
                >
                  (limpiar filtro)
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}
